// src/pages/fleet/permissions.ts — Fleet permission keys and helpers.
// Keep in sync with backend/src/config/fleetPermissions.js (the API enforces them).
import type { FleetPermission, User } from '../../types';

export const PERMISSIONS: { key: FleetPermission; label: string; hint: string }[] = [
  { key: 'dashboard',   label: 'Dashboard',   hint: 'Live map and fleet KPIs' },
  { key: 'create_trip', label: 'Create Trip', hint: 'Create and edit trips' },
  { key: 'trip_list',   label: 'Trip List',   hint: 'View trips; start, complete, cancel' },
  { key: 'master',      label: 'Master',      hint: 'Assets, Vehicle, State, District' },
  { key: 'reports',     label: 'Reports',     hint: 'Fleet reports and exports' },
  { key: 'alerts',      label: 'Alerts',      hint: 'View and resolve alerts' },
  { key: 'ifms',        label: 'IFMS',        hint: 'IFMS Queue and reporting status' },
  { key: 'admin',       label: 'Administration', hint: 'Manage Roles and Users' },
];

export const permissionLabel = (k: string) => PERMISSIONS.find((p) => p.key === k)?.label || k;

const ALL = PERMISSIONS.map((p) => p.key);

/** Effective permissions; staff accounts from before roles existed keep full access. */
export function permissionsOf(user: User | null | undefined): FleetPermission[] {
  if (!user) return [];
  if (user.role === 'SUPER_ADMIN' || user.role === 'GOVT_OFFICER') return ALL;
  return user.permissions || [];
}

export const can = (user: User | null | undefined, perm: FleetPermission) => permissionsOf(user).includes(perm);

// Where to land after sign-in, or when a page is not allowed.
const HOME_ORDER: [FleetPermission, string][] = [
  ['dashboard', '/fleet/dashboard'], ['trip_list', '/fleet/trips'], ['create_trip', '/fleet/trips/new'],
  ['alerts', '/fleet/alerts'], ['master', '/fleet/master/vehicles'], ['reports', '/fleet/reports'], ['ifms', '/fleet/ifms'], ['admin', '/fleet/admin/users'],
];
export function fleetHome(user: User | null | undefined): string | null {
  const have = permissionsOf(user);
  return HOME_ORDER.find(([p]) => have.includes(p))?.[1] ?? null;
}
